"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

export default function CoverSection() {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const elements = [badgeRef.current, titleRef.current, subtitleRef.current, ctaRef.current];
    elements.forEach((el, i) => {
      if (!el) return;
      el.style.opacity = "0";
      el.style.transform = "translateY(30px)";
      setTimeout(() => {
        if (!el) return;
        el.style.transition = "opacity 1s cubic-bezier(0.16,1,0.3,1), transform 1s cubic-bezier(0.16,1,0.3,1)";
        el.style.opacity = "1";
        el.style.transform = "translateY(0)";
      }, 200 + i * 200);
    });
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="couverture"
      className="relative min-h-screen flex items-center overflow-hidden">

      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="/assets/images/Ajouter_un_titre-1771683848983.jpg"
          alt="Guide GîteZen — De la gestion artisanale à la conciergerie structurée, couverture du guide 2026"
          fill
          className="object-cover object-center"
          priority />

        <div className="cover-overlay absolute inset-0" />
      </div>

      {/* Decorative vertical text */}
      <div className="absolute left-8 top-1/2 -translate-y-1/2 z-10 hidden xl:block">
        <span
          style={{
            writingMode: "vertical-rl",
            textOrientation: "mixed",
            letterSpacing: "0.3em",
            fontSize: "10px",
            fontWeight: 700,
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.4)"
          }}>

          GîteZen Guide 2026
        </span>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-8xl mx-auto px-6 md:px-16 w-full py-32">
        <div className="max-w-3xl">
          {/* Badge */}
          <div ref={badgeRef} className="mb-8">
            <span
              className="inline-block text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full"
              style={{
                background: "rgba(201,168,76,0.2)",
                border: "1px solid rgba(201,168,76,0.4)",
                color: "#E8C97A"
              }}>ÉDITION CONCIERGERIES 3+ LOGEMENTS · GÎTEZEN PRO


            </span>
          </div>

          {/* Title */}
          <h1
            ref={titleRef}
            className="font-serif text-white leading-tight mb-8"
            style={{ fontSize: "clamp(2.2rem, 5.5vw, 4.5rem)" }}>

            De la gestion artisanale{" "}
            <span className="italic" style={{ color: "#E8C97A" }}>
              à la conciergerie
            </span>{" "}
            structurée
          </h1>

          {/* Subtitle */}
          <p
            ref={subtitleRef}
            className="text-lg leading-relaxed mb-12"
            style={{ color: "rgba(255,255,255,0.75)", maxWidth: "540px" }}>

            Le guide 2026 pour industrialiser l'expérience voyageurs et sécuriser vos avis 5 étoiles
          </p>

          {/* CTA */}
          <div ref={ctaRef} className="flex flex-col sm:flex-row gap-4 items-start">
            <button
              onClick={() => scrollTo("introduction")}
              className="btn-primary"
              style={{ background: "linear-gradient(135deg, #C9A84C, #E8C97A)", color: "#1C1917" }}>

              Lire le guide →
            </button>
            <button
              onClick={() => scrollTo("diagnostic")}
              className="btn-secondary"
              style={{ borderColor: "rgba(255,255,255,0.4)", color: "white" }}>

              Mon auto-diagnostic
            </button>
          </div>

          {/* Stats row */}
          <div className="flex flex-wrap gap-12 mt-16 pt-10" style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}>
            {[
            { value: "10+", label: "Logements" },
            { value: "4", label: "Piliers clés" },
            { value: "5★", label: "Avis cibles" }].
            map((stat) =>
            <div key={stat.label}>
                <div
                className="font-serif text-3xl font-bold"
                style={{ color: "#E8C97A" }}>

                  {stat.value}
                </div>
                <div
                className="text-xs font-bold uppercase tracking-widest mt-1"
                style={{ color: "rgba(255,255,255,0.5)" }}>

                  {stat.label}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
        <span
          className="text-xs font-bold uppercase tracking-widest"
          style={{ color: "rgba(255,255,255,0.4)" }}>

          Défiler
        </span>
        <div className="w-px h-10 bg-gradient-to-b from-transparent to-white/30" />
      </div>
    </section>);

}